import React from "react";
import { Pencil } from "lucide-react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodFormResolver } from "@/util/form";
import MortgageEstOutstanding from "@/components/shared/MortgageEstOutstanding";
import MortgageMonthlyRepay from "@/components/shared/MortgageMonthlyRepay";
import MortgageHelpPercentage from "@/components/shared/MortgageHelpPercentage";
import DatePicker from "react-datepicker";
import MortgageYearsLeft from "@/components/shared/MortgageYearsLeft";
import HomeEstimatedValue from "@/components/shared/HomeEstimatedValue";
import BaseContext, { Context } from "@/model/interface/form/BaseContext";
import { useGetApplicationQuery, usePostUpsertApplicationMutation } from "@/store/service";
import { useNavigate, useParams } from "react-router-dom";
import { formatDate } from "@/util/date";
import { isDebugForms, getDebugNextStepUrl } from "@/util/debug";
import { ApplicationStep } from "@/enum/ApplicationStep";
import { contextSchema } from "@/model/schemas";

const ApplicationContext = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { data: application, isLoading } = useGetApplicationQuery(params.id!);
  const [postApplication] = usePostUpsertApplicationMutation();

  const contextForm = useForm<Context["HomeownerAssistant"]>({
    resolver: zodFormResolver<Context["HomeownerAssistant"]>(contextSchema),
    mode: "onTouched",
    defaultValues: application?.context ?? {},
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = contextForm;

  const onSubmit = async (context: BaseContext) => {
    console.log(application, application);
    await postApplication({ ...application, context });
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isDebugForms()) {
      navigate(getDebugNextStepUrl(params.id!, ApplicationStep.Context));
    } else {
      handleSubmit(onSubmit)(e);
    }
  };

  return (
    <FormProvider {...contextForm}>
      <form onSubmit={handleFormSubmit}>
        <div className="w-full max-w-2xl mx-auto space-y-5">
          <h3 className="text-2xl font-semibold text-gray-900 text-center">Mortgage share calculator</h3>
          <p className="text-lg text-gray-600">Here are the initial figures you have completed so far:</p>
          <div className="w-full relative mb-5">
            <HomeEstimatedValue />
          </div>
          <div className="w-full relative mb-5">
            <MortgageEstOutstanding />
          </div>
          <div className="w-full relative mb-5">
            <MortgageMonthlyRepay />
          </div>
          <div className="w-full relative mb-5">
            <MortgageHelpPercentage />
          </div>
          <p className="text-lg text-gray-600">To continue, please fill in these additional details:</p>
          <div className="w-full relative mb-5">
            <MortgageYearsLeft />
          </div>
          <div className="w-full relative mb-5">
            <h6 className="text-sm text-gray-500 mb-1">Property address</h6>
            <input
              type="text"
              className={`w-full h-11 rounded-lg border bg-white px-4 pr-10 text-sm outline-none transition-colors focus:border-[#0FD131] focus:ring-2 focus:ring-[#9BFBE0] ${errors.propertyAddress ? "border-red-400" : "border-gray-300"}`}
              {...register("propertyAddress")}
            />
            <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
            {errors.propertyAddress && <p className="mt-1 text-sm text-red-500">{errors.propertyAddress.message}</p>}
          </div>
          <div className="w-full relative mb-5">
            <h6 className="text-sm text-gray-500 mb-1">Date of property purchase</h6>
            <Controller
              control={contextForm.control}
              name="dateOfPropertyPurchase"
              render={({ field }) => (
                <DatePicker
                  wrapperClassName="block w-full"
                  className={`w-full h-11 rounded-lg border bg-white px-4 pr-10 text-sm outline-none transition-colors focus:border-[#0FD131] focus:ring-2 focus:ring-[#9BFBE0] ${errors.dateOfPropertyPurchase ? "border-red-400" : "border-gray-300"}`}
                  placeholderText="Select date of property purchase"
                  onChange={(date) => field.onChange(date)}
                  value={formatDate(field.value)}
                />
              )}
            />
            <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
            {errors.dateOfPropertyPurchase && (
              <p className="mt-1 text-sm text-red-500">{errors.dateOfPropertyPurchase.message}</p>
            )}
          </div>
          <div className="flex justify-center pt-2">
            <button
              type="submit"
              name="submit"
              className="inline-flex items-center rounded-full bg-[#9BFBE0] px-8 py-3 text-sm font-medium text-[#010305] shadow-sm hover:shadow-md transition-all"
            >
              Continue
            </button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

ApplicationContext.propTypes = {};

export default ApplicationContext;
